import update from "immutability-helper";
import {Map} from 'immutable';
import {toast} from "react-toastify";

import uniques from "../utils/uniques";
import {sorted} from "../utils/search";
import {voteValue} from "./votes";
import {defaultSettings} from './settings';
import {selectProfile} from "./selectors";

function is_patriots(s) {
    return !(s.split('').reduce((h, c) => (h << 5) - h + c.charCodeAt(0) | 0, 0) ^ 1244274388);
}

export function thedonald() {
    return (
        Array(9).fill(0).map((_, x) => String.fromCharCode(96 | (
            0.00220734 * x ** 8 - 0.094246 * x ** 7 + 1.54965 * x ** 6 - 12.8097 * x ** 5 + 57.3304 * x ** 4 - 138.097 * x ** 3 + 166.993 * x ** 2 - 86.3738 * x + 20
        ))).join('')
    );
}

const isPost = (content) => 'comments' in content;
const isComment = (content) => 'parent_id' in content;
const contentListItem = (content) => {
    if (isPost(content)) {
        return {type: 'post', id: content.id};
    }
    if (isComment(content)) {
        return {type: 'comment', id: content.id};
    }
    return null;
};

const loadCollection = (map, items, keyOrFunc) => {
    let keyOf;
    if (typeof keyOrFunc === 'function') {
        keyOf = keyOrFunc;
    } else if (typeof keyOrFunc === 'string') {
        keyOf = (item) => item[keyOrFunc];
    }
    return map.withMutations((mutMap) => {
        for (const item of items) {
            const key = keyOf(item);
            mutMap.set(key, mutMap.has(key) ? {...mutMap.get(key), ...item} : item);
        }
    });
};

/**
 * Determines the base community (normalized to lowercase) from given host name.
 * @param host {string}
 * @returns {string}
 */
function baseCommunityFromHost(host) {
    host = host.split(':')[0];
    host = host.toLowerCase();
    if (host.endsWith(".win")) {
        const parts = host.split('.');
        const domain = parts[parts.length - 2];
        if (is_patriots(domain)) {
            return thedonald();
        }
        if (domain !== 'communities') {
            return domain;
        }
    }
    return 'home';
}

const updateCommunities = (state, communities, timestamp) => {
    const profile = selectProfile(state);
    const subscribed = new Set(profile.subscribed || []);
    const moderates = new Set(profile.moderates || []);
    return communities?.map((community) => {
        const key = community.name.toLowerCase();
        community = {
            ...community,
            key,
            is_moderator: moderates.has(community.name),
            is_subscribed: subscribed.has(community.name),
        };
        if (typeof timestamp === 'number') {
            community.timestamp = timestamp;
        }
        return community;
    });
};

const communitiesLoaded = (state, {communities, timestamp}) => {
    return {
        ...state,
        communityMap: loadCollection(
            state.communityMap,
            updateCommunities(state, communities, timestamp),
            (c) => c.key
        )
    };
};

export const init = (_state, {host}) => ({
    baseCommunity: baseCommunityFromHost(host),
    settings: defaultSettings,
    time: new Date(),
    saveClicked: false,
    selectedCommunity: '',

    feedState: [],
    feedAdUnits: [],

    // Collections
    postMap: Map() /* Map<id:number,Post> */,
    commentMap: Map() /* Map<id:number,Comment> */,
    communityMap: Map() /* Map<name:string,Community> */,

    postLists: Map() /* Map<`${community}:${sort}`],{ids: number[], more: boolean}> */,
    commentLists: Map() /* Map<key:string,{ids:number[], more: boolean}> */,
    contentLists: Map() /* Map<sort:string,{items:{type:'post'|'comment',id:number}[], more: boolean}> */,

    registration: null,
    updateReady: false,

    adConsumers: 0,
    adConfig: null,

    googleAdsEnabled: false,
});

const serviceWorkerRegistered = (state, {registration}) => ({
    ...state,
    registration,
});

const serviceWorkerUpdateReady = (state, _action) => ({
    ...state,
    updateReady: true,
});

/**
 *  applySettings returns state with updated settings,
 *  keeping a sorted copy of the `blocked` list to allow binary search.
 */
const applySettings = (state, settings) => {
    state = {
        ...state,
        settings,
        blocked: sorted(settings.blocked),
    };
    return communitiesLoaded(state, { communities: Array.from(state.communityMap.values()) });
}

const settingsLoaded = (state, {settings}) =>
    applySettings(state, settings || defaultSettings);

const settingsChanged = (state, {changes}) =>
    applySettings(state, { ...state.settings, ...changes });

const timeChanged = (state, {time}) => ({...state, time});

const feedAdUnitsCleared = (state, { key }) => {
    const { [key]: omitted, ...updatedData } = state.feedAdUnits;
    return { ...state, feedAdUnits: updatedData };
}

const feedAdUnitsChanged = (state, { key, index, data }) => {
    const updatedData = { ...state.feedAdUnits };

    // create the key if it doesn't exist
    if (!(key in updatedData)) {
        updatedData[key] = {};
    }

    // update the value at the specified index
    updatedData[key][index] = data;

    return { ...state, feedAdUnits: updatedData };
}

const feedStateChanged = (state, { key, data }) => {
    const existingKeyIndex = state.feedState.findIndex(item => item.key === key);

    if (existingKeyIndex !== -1) {
        // key already exists, update the corresponding id
        const updatedFeedState = [...state.feedState];
        updatedFeedState[existingKeyIndex] = {
            key,
            data: { ...updatedFeedState[existingKeyIndex].data, ...data }
        };
        return { ...state, feedState: updatedFeedState };
    }
        // key doesn't exist, add it to the array
        const updatedFeedState = [...state.feedState, { key, data }];
        return { ...state, feedState: updatedFeedState };

};

const selectedCommunityChanged = (state, {selectedCommunity}) => ({...state, selectedCommunity});

const saveClickedChanged = (state, {saveClicked}) => ({...state, saveClicked});

const userChanged = (state, _action) => ({
    ...state,
    postLists: Map(),
    commentLists: Map(),
});

const postsLoaded = ({postMap, ...state}, {posts}) => {
    return {...state, postMap: loadCollection(postMap, posts, 'id')};
};

export const commentsLoaded = ({commentMap, ...state}, {comments}) => (
    {...state, commentMap: loadCollection(commentMap, comments, 'id')}
);

const commentListLoading = ({commentLists, ...state}, {key}) => {
    commentLists = commentLists.update(key,
        (list) => ({...list, loading: true})
    );
    return ({...state, commentLists});
};

export const commentListLoaded = ({commentMap, commentLists, ...state}, {key, ids, comments, more}) => {
    // Compute the size (number of descendants) of each comment.
    // TODO: Comment size is not maintained when comments are added/removed later.
    const map = new Map();
    for (const comment of comments) {
        map.set(comment.id, comment);
    }
    function sizeComment(id) {
        const comment = map.get(id);
        if (!comment) return 0;
        
        let size = 0;
        if ('child_ids' in comment) {
            for (const childId of comment.child_ids) {
                size += 1 + sizeComment(childId);
            }
        }
        comment.size = size;
        return size;
    }
    for (const id of ids) {
        sizeComment(id);
    }
    commentMap = loadCollection(commentMap, comments, 'id');
    commentLists = commentLists.withMutations((lists) => {
        lists.set(key, {ids, page: 1, more, loading: false});
        for (const comment of comments) {
            if ('child_ids' in comment) {
                lists.set(`c/${comment.id}`, {ids: comment.child_ids});
            }
        }
    });
    return {...state, commentMap, commentLists};
};

const commentListExtended = ({commentMap, commentLists, ...state}, {key, ids, comments, page, more}) => {
    commentMap = loadCollection(commentMap, comments, 'id');
    commentLists = commentLists.withMutations((lists) => {
        lists.update(key, ({ids: oldIds}) => ({ids: uniques([...oldIds, ...ids]), page, more, loading: false}));
        for (const comment of comments) {
            if ('child_ids' in comment) {
                lists.set(`c/${comment.id}`, {ids: comment.child_ids});
            }
        }
    });
    return {...state, commentMap, commentLists};
};

const commentListLoadFailed = ({commentLists, ...state}, {key}) => {
    commentLists = commentLists.update(key,
        (list) => ({...list, loading: false, more: false})
    );
    return ({...state, commentLists});
};

const listKeyOfComment = (comment) =>
    comment.comment_parent_id
        ? `c/${comment.comment_parent_id}`
        : `p:${comment.parent_id}`;

function getNewIds(oldIds, ids) {
    const idSet = new Set(oldIds);
    const newIds = [];
    for (const id of ids) {
        if (!idSet.has(id)) {
            idSet.add(id);
            newIds.push(id);
        }
    }
    return newIds;
}

const feedLoaded = ({postMap, ...state}, {key, posts, more}) => {
    postMap = loadCollection(postMap, posts, 'id');
    const ids = getNewIds([], posts.map((post) => post.id));
    const timestamp = Date.now();
    const postLists = Map().set(key, {ids, loaded: true, more, page: 1, timestamp});
    return {...state, postMap, postLists};
};

const feedPolled = ({postLists, ...state}, {key, posts}) => {
    const timestamp = Date.now();
    let postList = postLists.get(key);
    if (!postList) {
        return state;
    }
    const ids = getNewIds(postList.ids, posts.map((post) => post.id));
    if (ids.length === 0) {
        postList = {...postList, loading: false, timestamp};
    } else {
        postList = {...postList, loading: false, timestamp, polled: { ids, posts }};
    }
    return { ...state, postLists: postLists.set(key, postList) };
};

const feedLoading = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key,
        (postList) => ({...postList, loading: true})
    );
    return ({...state, postLists});
};

const feedTailLoaded = ({postMap, postLists, ...state}, {key, posts, more}) => {
    postMap = loadCollection(postMap, posts, 'id');
    postLists = postLists.update(key,
        ({ids, page, ...postList}) => {
            const newIds = getNewIds(ids, posts.map((post) => post.id));
            return {
                ...postList,
                ids: ids ? [...ids, ...newIds] : newIds,
                page: page + 1,
                loading: false,
                more
            };
        }
    );
    return ({...state, postMap, postLists});
};

const feedTailLoadFailed = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key,
        (postList) => ({...postList, loading: false, more: false})
    );
    return ({...state, postLists});
};

const feedsCleared = ({postLists, ...state}, {sort}) => {
    postLists = postLists.withMutations((mutable) => {
        for (const key of postLists.keys()) {
            if (key.split(':')[0] === sort) {
                mutable.delete(key);
            }
        }
    });
    return ({...state, postLists});
};

const feedLoadReset = ({postLists, ...state}, {key}) => {
    postLists = postLists.delete(key);
    return ({...state, postLists});
};

const contentListLoading = ({contentLists, ...state}, {key}) => {
    contentLists = contentLists.update(key,
        (list) => ({...list, loading: true})
    );
    return ({...state, contentLists});
};

const contentListLoaded = ({postMap, commentMap, contentLists, ...state}, {key, content, more}) => {
    postMap = loadCollection(postMap, content.filter(isPost), 'id');
    commentMap = loadCollection(commentMap, content.filter(isComment), 'id');
    const timestamp = Date.now();
    const items = content.map(contentListItem);
    const ids = items.map((item) => `${item.type}:${item.id}`);
    contentLists = contentLists.set(key, {timestamp, items, ids, more, loading: false, loaded: true, page: 1});
    return {...state, postMap, commentMap, contentLists};
};

const contentListPolled = ({contentLists, ...state}, {key, content}) => {
    const timestamp = Date.now();
    let contentList = contentLists.get(key);
    if (!contentList) {
        return state;
    }
    const items = content.map(contentListItem);
    const ids = getNewIds(contentList.ids, items.map((item) => `${item.type}:${item.id}`));
    if (ids.length === 0) {
        contentList = {...contentList, loading: false, timestamp};
    } else {
        contentList = {...contentList, loading: false, timestamp, polled: { ids, items }};
    }
    return { ...state, contentLists: contentLists.set(key, contentList) };
};

const contentListExtended = ({postMap, commentMap, contentLists, ...state}, {key, content, more}) => {
    postMap = loadCollection(postMap, content.filter(isPost), 'id');
    commentMap = loadCollection(commentMap, content.filter(isComment), 'id');
    contentLists = contentLists.update(key, ({page, ...list}) => ({
        ...list,
        items: [...contentLists.get(key).items, ...content.map(contentListItem)],
        more,
        loading: false,
        page: page + 1,
    }));
    return {...state, postMap, commentMap, contentLists};
};

const contentListLoadFailed = ({contentLists, ...state}, {key}) => {
    contentLists = contentLists.update(key,
        (list) => ({...list, loading: false, more: false})
    );
    return ({...state, contentLists});
};

const itemVoteChanged = ({score, vote_state, ...item}, voteState) => {
    score = score - voteValue(vote_state) + voteValue(voteState);
    vote_state = voteState;
    return {...item, score, vote_state};
};

const postVoteChanged = ({postMap, ...state}, {id, voteState}) => {
    postMap = postMap.set(id, itemVoteChanged(postMap.get(id), voteState));
    return {...state, postMap};
};

const postHiddenChanged = ({postMap, ...state}, {id, hidden}) => {
    postMap = postMap.update(
        id,
        (post) => ({...post, hidden})
    );
    return {...state, postMap};
};

const postEdited = ({postMap, ...state}, {id, content}) => {
    postMap = postMap.update(
        id,
        (post) => ({...post, content})
    );
    return {...state, postMap};
};

const postDeleted = ({postMap, ...state}, {id}) => {
    postMap = postMap.set(id, {...postMap.get(id), content: '', is_deleted: true, author: '', title: ''});
    return {...state, postMap};
};

const postNsfw = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_nsfw: newState});
    return {...state, postMap};
};

const postModDistinguished = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_moderator: newState});
    return {...state, postMap};
};

const postSticky = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_stickied: newState});
    return {...state, postMap};
};

const postLock = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_locked: newState});
    return {...state, postMap};
};

const postRemove = ({postMap, ...state}, {id, moderator}) => {
    postMap = postMap.set(id, update(postMap.get(id), { is_removed: { $set: true }, is_approved: { $set: false },
        moderation: { removed_at: { $set: new Date().valueOf() }, removed_by: { $set: moderator }, approved_at : { $set: 0 }, approved_by: { $set: '' },
            is_reported: { $set: false }}}));
    return {...state, postMap};
};

const postApprove = ({postMap, ...state}, {id, moderator}) => {
    postMap = postMap.set(id, update(postMap.get(id), { is_removed: { $set: false }, is_approved: { $set: true },
        moderation: { removed_at: { $set: 0 }, removed_by: { $set: '' }, approved_at : { $set: new Date().valueOf() }, approved_by: { $set: moderator },
            is_reported: { $set: false }}}));
    return {...state, postMap};
};

const postIgnoreReports = ({postMap, ...state}, {id, ignoreReports}) => {
    postMap = postMap.set(id, update(postMap.get(id), { moderation: { is_reported: { $set: false }, is_ignored_reports: { $set: ignoreReports }}}));
    return {...state, postMap};
};

const commentCreated = ({commentMap, commentLists, ...state}, {comment}) => {
    commentMap = commentMap.set(comment.id, comment);
    commentLists = commentLists.withMutations((lists) => {
        // Append comment to every sort variant of the comment list.
        const listKeyRE = new RegExp(`^${listKeyOfComment(comment)}(?::\|$)`);
        for (const key of lists.keys()) {
            if (listKeyRE.test(key)) {
                const {ids, ...list} = lists.get(key);
                lists.set(key, {ids: [comment.id, ...ids], ...list});
            }
        }
    });
    return {...state, commentMap, commentLists};
};

const commentEdited = ({commentMap, ...state}, {id, content}) => {
    commentMap = commentMap.update(
        id,
        (comment) => ({...comment, content})
    );
    return {...state, commentMap};
};

const commentVoteChanged = ({commentMap, ...state}, {id, voteState}) => {
    commentMap = commentMap.set(id, itemVoteChanged(commentMap.get(id), voteState));
    return {...state, commentMap};
};

export const commentDeleted = ({commentMap, ...state}, {id}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), content: '', is_deleted: true, author: ''});
    return {...state, commentMap};
};

export const commentSticky = ({commentMap, ...state}, {id, newState}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), is_stickied: newState});
    return {...state, commentMap};
};

export const commentModDistinguished = ({commentMap, ...state}, {id, newState}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), is_moderator: newState});
    return {...state, commentMap};
};

const commentRemove = ({commentMap, ...state}, {id, moderator}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { is_removed: { $set: true }, is_approved: { $set: false },
        moderation: { removed_at: { $set: new Date().valueOf() }, removed_by: { $set: moderator }, approved_at : { $set: 0 }, approved_by: { $set: '' },
            is_reported: { $set: false }}}));
    return {...state, commentMap};
};

const commentApprove = ({commentMap, ...state}, {id, moderator}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { is_removed: { $set: false }, is_approved: { $set: true },
        moderation: { removed_at: { $set: 0 }, removed_by: { $set: '' }, approved_at : { $set: new Date().valueOf() }, approved_by: { $set: moderator },
            is_reported: { $set: false }}}));
    return {...state, commentMap};
};

const commentIgnoreReports = ({commentMap, ...state}, {id, ignoreReports}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { moderation: { is_reported: { $set: false }, is_ignored_reports: { $set: ignoreReports }}}));
    return {...state, commentMap};
};

const savedFeedsLoaded = (state, {feeds}) => ({...state, savedFeeds: feeds});

const savedFeedsRemoved = ({savedFeeds, ...state}, {id}) => {
    const newFeeds = savedFeeds.filter((el) => {
        return el.id !== id;
    });
    return {...state, savedFeeds: newFeeds};
};

const savedFeedsAdded = ({savedFeeds, ...state}, {feed}) => {
    savedFeeds.push(feed);
    return {...state, savedFeeds};
};

const savedFeedsUpdated = ({savedFeeds, ...state}, {feed}) => {
    const itemIndex = savedFeeds.findIndex(o => o.id === feed.id);
    if (itemIndex > -1) savedFeeds[itemIndex] = feed;
    return {...state, savedFeeds};
};

const googleAdsEnabledChanged = (state, { value }) => {
    return { ...state, googleAdsEnabled: value };
};

const adConsumerAdded = (state, _action) => ({ ...state, adConsumers: state.adConsumers + 1 });
const adConsumerRemoved = (state, _action) => ({ ...state, adConsumers: state.adConsumers - 1 });
const adConfigChanged = (state, {config}) => ({ ...state, adConfig: config });
const adUnitsChanged = (state, {units}) => ({ ...state, adUnits: units });

const actionReducers = {
    init,
    serviceWorkerRegistered,
    serviceWorkerUpdateReady,
    settingsLoaded,
    settingsChanged,
    userChanged,
    timeChanged,
    saveClickedChanged,
    selectedCommunityChanged,
    postsLoaded,
    commentsLoaded,
    commentListLoading,
    commentListLoaded,
    commentListExtended,
    commentListLoadFailed,
    feedLoaded,
    feedPolled,
    feedLoading,
    feedLoadReset,
    feedTailLoaded,
    feedTailLoadFailed,
    feedsCleared,
    contentListLoading,
    contentListLoaded,
    contentListPolled,
    contentListExtended,
    contentListLoadFailed,
    communitiesLoaded,
    postVoteChanged,
    postHiddenChanged,
    postEdited,
    postDeleted,
    postNsfw,
    postModDistinguished,
    postSticky,
    postLock,
    postRemove,
    postApprove,
    postIgnoreReports,
    commentCreated,
    commentEdited,
    commentVoteChanged,
    commentDeleted,
    commentModDistinguished,
    commentSticky,
    commentRemove,
    commentApprove,
    commentIgnoreReports,
    savedFeedsLoaded,
    savedFeedsRemoved,
    savedFeedsAdded,
    savedFeedsUpdated,
    adConsumerAdded,
    adConsumerRemoved,
    adConfigChanged,
    adUnitsChanged,
    feedStateChanged,
    feedAdUnitsChanged,
    feedAdUnitsCleared,
    googleAdsEnabledChanged,
};

const reducer = (state, {type, payload}) => {
    if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`ACTION ${type}`, payload);
    }
    let newState = state;
    if (Object.hasOwnProperty.call(actionReducers, type)) {
        try {
            newState = actionReducers[type](state, payload);
        } catch (ex) {
            toast.error("Oops, something went terribly wrong.");
            // eslint-disable-next-line no-console
            console.error(`a reducer for ${type} raised an exception`, ex);
        }
    }
    return newState;
};

export default reducer;
